// IND-024: secciones nuevas del detalle del material (previsión, quién lo
// consume, pestaña Consumo y pestaña Entradas y compras). Se rellenan al
// llegar el detalle; mientras, «Calculando consumo…» (IND-026).
import type { ReactNode } from 'react';

import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { MaterialDetail } from '../services/industrialCostService';
import type { MaterialForecast } from '../utils/materialForecast';
import { rotationGapText } from '../utils/materialRotation';
import { esNumberFormat } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import SortableHeaderCell, { TEN_ROWS_MAX_HEIGHT } from '../components/SortableHeaderCell';

const numberFormat = esNumberFormat({ maximumFractionDigits: 1 });
const pctFormat = esNumberFormat({ maximumFractionDigits: 0 });

export function fmtQty(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return numberFormat.format(value);
}

export function fmtPct(value: number | null | undefined, signed = false): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const text = `${pctFormat.format(Math.abs(value))} %`;
  if (!signed) return text;
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text;
}

export function fmtDay(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('es-ES');
}

function shortDay(value: string): string {
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  return parsed.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function monthLabel(month: string): string {
  const [year, number] = month.split('-');
  return `${MONTHS[Number(number) - 1] ?? number} ${year.slice(2)}`;
}

const BASIS_UNIT: Record<string, string> = { per_unit: 'm', per_kg: 'kg' };

function SectionTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontWeight: 950, fontSize: 13.5 }}>{title}</Typography>
      {sub ? <Typography sx={{ color: '#94a3b8', fontSize: 10.5 }}>{sub}</Typography> : null}
    </Box>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2.5, minWidth: 0 }}>
      {children}
    </Paper>
  );
}

const LOADING_TEXT = 'Calculando consumo…';

// IND-026: hueco que espera al detalle, con spinner y texto visibles.
export function DetailLoading({ height = 220 }: { height?: number }) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        height,
        flexShrink: 0,
        borderRadius: 2.5,
        border: '1px dashed rgba(148,163,184,0.25)',
        bgcolor: 'rgba(148,163,184,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      <CircularProgress size={18} />
      <Typography sx={{ color: '#cbd5e1', fontSize: 12.5, fontWeight: 800 }}>{LOADING_TEXT}</Typography>
    </Box>
  );
}

/** Lo mismo, en línea, para el valor de una cifra. */
export function LoadingValue() {
  return (
    <Box component="span" role="status" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <CircularProgress size={14} />
      <Box component="span" sx={{ color: '#cbd5e1', fontSize: 12, fontWeight: 800 }}>
        {LOADING_TEXT}
      </Box>
    </Box>
  );
}

/** Motivo del fallo del detalle, en palabras de la planta. */
export function detailErrorReason(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return 'El cálculo del consumo ha tardado más de 30 segundos. La primera apertura tras una carga es la más lenta; al reintentar suele salir enseguida.';
    }
    const status = error.response?.status;
    if (!status) return 'No hay conexión con el servidor.';
    if (status === 401 || status === 403) return 'Tu usuario no tiene permiso para ver este detalle.';
    if (status === 404) return 'El material ya no está activo en los datos de planta.';
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    return typeof detail === 'string' && detail.trim()
      ? `El servidor respondió con un error (${status}): ${detail.trim()}`
      : `El servidor respondió con un error (${status}).`;
  }
  return 'Error inesperado al calcular el consumo.';
}

export function DetailError({
  error,
  onRetry,
  retrying = false,
}: {
  error: unknown;
  onRetry: () => void;
  retrying?: boolean;
}) {
  return (
    <Alert
      severity="warning"
      variant="outlined"
      sx={{ fontSize: 12, flexShrink: 0, alignItems: 'center' }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={onRetry}
          disabled={retrying}
          startIcon={retrying ? <CircularProgress size={12} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 850 }}
        >
          Reintentar
        </Button>
      }
    >
      {`No se ha podido cargar el consumo del material. ${detailErrorReason(error)} El resto del detalle sigue siendo válido.`}
    </Alert>
  );
}

function Figure({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2, minWidth: 0 }}>
      <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 800 }}>{label}</Typography>
      <Typography sx={{ mt: 0.3, fontSize: 17, fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {sub ? <Typography sx={{ color: 'text.secondary', fontSize: 10.5, mt: 0.3 }}>{sub}</Typography> : null}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Resumen: previsión y quién lo consume
// ---------------------------------------------------------------------------

export function ForecastSection({
  forecast,
  unit,
  hasRhythm,
  rhythmWeeks,
}: {
  forecast: MaterialForecast | null;
  unit: string;
  hasRhythm: boolean;
  rhythmWeeks: number;
}) {
  return (
    <Section>
      <SectionTitle
        title="Previsión a 8 semanas"
        sub={
          hasRhythm
            ? `Solo OF confirmadas y con el ritmo habitual de consumo (media de las últimas ${rhythmWeeks} semanas por día laborable). Compras marcadas en su fecha.`
            : `Solo OF confirmadas: sin consumo en las últimas ${rhythmWeeks} semanas no hay ritmo habitual.`
        }
      />
      {forecast ? (
        <Box sx={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={forecast.points} margin={{ top: 8, right: 16, bottom: 0, left: 4 }}>
              <CartesianGrid stroke="rgba(148,163,184,0.12)" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDay}
                tick={{ fill: '#94a3b8', fontSize: 10 }}
                interval={6}
              />
              <YAxis
                tickFormatter={(value: number) => fmtQty(value)}
                tick={{ fill: '#94a3b8', fontSize: 10 }}
                width={64}
              />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 11 }}
                labelFormatter={(value: string) => fmtDay(value)}
                formatter={(value: number, name: string) => [`${fmtQty(value)} ${unit}`.trim(), name]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#f87171" strokeDasharray="4 4" />
              <Line
                type="stepAfter"
                dataKey="ofBalance"
                name="Solo OF confirmadas"
                stroke="#38bdf8"
                dot={false}
                strokeWidth={2}
                isAnimationActive={false}
              />
              {hasRhythm ? (
                <Line
                  type="monotone"
                  dataKey="rhythmBalance"
                  name="Con el ritmo habitual"
                  stroke="#fbbf24"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ) : null}
              {forecast.purchases.map((purchase) => {
                const point = forecast.points.find((item) => item.date === purchase.date);
                return point ? (
                  <ReferenceDot
                    key={purchase.date}
                    x={purchase.date}
                    y={point.ofBalance}
                    r={5}
                    fill="#4ade80"
                    stroke="#0f172a"
                    label={{ value: `+${fmtQty(purchase.qty)}`, position: 'top', fill: '#4ade80', fontSize: 10 }}
                  />
                ) : null;
              })}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Sin stock calculado: no hay previsión.</Typography>
      )}
    </Section>
  );
}

function ShareRow({ label, qty, pct, unit }: { label: string; qty: number; pct: number | null; unit: string }) {
  return (
    <Box sx={{ py: 0.45 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography sx={{ fontSize: 11.5, minWidth: 0 }} noWrap title={label}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11.5, fontWeight: 850, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {fmtPct(pct)} · {fmtQty(qty)} {unit}
        </Typography>
      </Stack>
      <Box sx={{ mt: 0.3, height: 4, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.12)' }}>
        <Box sx={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%`, height: 4, borderRadius: 999, bgcolor: '#38bdf8' }} />
      </Box>
    </Box>
  );
}

export function ConsumersSection({ detail, unit }: { detail: MaterialDetail; unit: string }) {
  const recent = detail.consumers_recent;
  return (
    <Section>
      <SectionTitle title="Quién lo consume" sub="Últimas 8 semanas, en % del consumo del material" />
      {recent.total_qty > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography sx={{ color: '#cbd5e1', fontSize: 11, fontWeight: 900, mb: 0.5 }}>Productos</Typography>
            {recent.products.map((item) => (
              <ShareRow
                key={item.product_id}
                label={[item.product_code, item.product_name].filter(Boolean).join(' · ') || item.product_id}
                qty={item.consumed_qty}
                pct={item.pct}
                unit={unit}
              />
            ))}
            {recent.others ? (
              <ShareRow
                label={`Resto (${recent.others.count} ${recent.others.count === 1 ? 'producto' : 'productos'})`}
                qty={recent.others.consumed_qty}
                pct={recent.others.pct}
                unit={unit}
              />
            ) : null}
          </Box>
          <Box>
            <Typography sx={{ color: '#cbd5e1', fontSize: 11, fontWeight: 900, mb: 0.5 }}>Líneas</Typography>
            {recent.lines.map((item) => (
              <ShareRow
                key={item.line_id ?? 'sin-linea'}
                label={item.line_name ?? (item.line_id ? item.line_id : 'Sin línea en el parte')}
                qty={item.consumed_qty}
                pct={item.pct}
                unit={unit}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Sin consumo en las últimas 8 semanas.</Typography>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Pestaña Consumo
// ---------------------------------------------------------------------------

type MonthlyItem = MaterialDetail['monthly'][number];
type ConsumerYearItem = MaterialDetail['consumers_year'][number];

// Variación frente al mismo mes del año anterior (no en meses incompletos).
function monthVariation(item: MonthlyItem): number | null {
  return item.consumed_qty !== null &&
    item.previous_year_qty !== null &&
    item.previous_year_qty > 0 &&
    !item.in_progress &&
    !item.partial
    ? (item.consumed_qty / item.previous_year_qty - 1) * 100
    : null;
}

// IND-026: orden por cabecera. Por defecto, el mes más reciente arriba y los
// productos que más consumen.
type MonthSortKey = 'month' | 'consumed' | 'previous' | 'variation';
const MONTH_COLUMNS: Array<{ key: MonthSortKey; label: string; value: (item: MonthlyItem) => SortValue }> = [
  { key: 'month', label: 'Mes', value: (item) => item.month },
  { key: 'consumed', label: 'Consumo', value: (item) => item.consumed_qty },
  { key: 'previous', label: 'Año anterior', value: (item) => item.previous_year_qty },
  { key: 'variation', label: 'Variación', value: monthVariation },
];

type ConsumerSortKey = 'product' | 'line' | 'produced' | 'perUnit' | 'total';
function consumerProductLabel(item: ConsumerYearItem): string {
  return [item.product_code, item.product_name].filter(Boolean).join(' · ') || item.product_id;
}
const CONSUMER_COLUMNS: Array<{ key: ConsumerSortKey; label: string; value: (item: ConsumerYearItem) => SortValue }> = [
  { key: 'product', label: 'Producto', value: consumerProductLabel },
  { key: 'line', label: 'Línea', value: (item) => item.line_name ?? item.line_id },
  { key: 'produced', label: 'Producido', value: (item) => item.produced_qty },
  { key: 'perUnit', label: 'Consumo por unidad', value: (item) => item.consumption_per_unit },
  { key: 'total', label: 'Consumo total', value: (item) => item.consumed_qty },
];

export function ConsumptionTab({ detail, unit }: { detail: MaterialDetail; unit: string }) {
  const maxMonth = Math.max(
    1,
    ...detail.monthly.flatMap((item) => [item.consumed_qty ?? 0, item.previous_year_qty ?? 0]),
  );
  const ytd = detail.year_to_date;
  const monthSort = useTableSort<MonthSortKey>('month', 'desc');
  const monthColumn = MONTH_COLUMNS.find((column) => column.key === monthSort.key) ?? MONTH_COLUMNS[0];
  const months = sortRows(detail.monthly, monthColumn.value, monthSort.direction);
  const consumerSort = useTableSort<ConsumerSortKey>('total', 'desc');
  const consumerColumn = CONSUMER_COLUMNS.find((column) => column.key === consumerSort.key) ?? CONSUMER_COLUMNS[4];
  const consumers = sortRows(detail.consumers_year, consumerColumn.value, consumerSort.direction);
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
        <Figure
          label="Consumo medio por día laborable"
          value={`${fmtQty(detail.rhythm.per_workday)} ${unit}`.trim()}
          sub={`últimas ${detail.rhythm_weeks} semanas · ${detail.rhythm.workdays} días laborables`}
        />
        <Figure
          label="Media de 12 meses"
          value={`${fmtQty(detail.year_rate.per_workday)} ${unit}`.trim()}
          sub="por día laborable"
        />
        <Figure
          label="Últimas 4 semanas"
          value={fmtPct(detail.recent_variation_pct, true)}
          sub={`${fmtQty(detail.recent_rate.per_workday)} ${unit} por día laborable, sobre la media de 12 meses`}
        />
        <Figure
          label="Año hasta hoy"
          value={`${fmtQty(ytd.current_qty)} ${unit}`.trim()}
          sub={
            ytd.previous_qty === null
              ? 'sin el año anterior completo para comparar'
              : `${fmtPct(ytd.variation_pct, true)} frente al mismo periodo del año anterior (${fmtQty(ytd.previous_qty)})`
          }
        />
        {/* IND-025: coeficiente de variación del consumo semanal (26 semanas). */}
        <Figure
          label="Regularidad"
          value={REGULARITY_LABEL[detail.regularity.label]}
          sub={
            detail.regularity.cv !== null
              ? `variación semanal ${esNumberFormat({ maximumFractionDigits: 2 }).format(detail.regularity.cv)} · ${detail.regularity.weeks} semanas`
              : detail.regularity.label === 'few_data'
                ? `${detail.regularity.weeks} de 8 semanas mínimas`
                : `${detail.regularity.weeks} semanas`
          }
        />
      </Box>

      <RotationSection detail={detail} unit={unit} />

      <Section>
        <SectionTitle
          title="Consumo por mes"
          sub="Producción × escandallo, el mismo cálculo que el stock. En gris, el mismo mes del año anterior."
        />
        <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {MONTH_COLUMNS.map((column) => (
                  <SortableHeaderCell
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    sort={monthSort}
                    align={column.key === 'month' ? 'left' : 'right'}
                  />
                ))}
                <TableCell sx={{ width: '40%' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {months.map((item) => {
                const variation = monthVariation(item);
                return (
                  <TableRow key={item.month}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {monthLabel(item.month)}
                      {item.in_progress ? (
                        <Typography component="span" sx={{ color: '#94a3b8', fontSize: 10, ml: 0.75 }}>
                          en curso
                        </Typography>
                      ) : item.partial ? (
                        <Typography component="span" sx={{ color: '#94a3b8', fontSize: 10, ml: 0.75 }}>
                          mes incompleto
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 850, fontVariantNumeric: 'tabular-nums' }}>
                      {item.consumed_qty === null ? 'sin datos' : fmtQty(item.consumed_qty)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                      {item.previous_year_qty === null ? 'sin datos' : fmtQty(item.previous_year_qty)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtPct(variation, true)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.12)', mb: 0.4 }}>
                        <Box sx={{ width: `${((item.consumed_qty ?? 0) / maxMonth) * 100}%`, height: 6, borderRadius: 999, bgcolor: '#38bdf8' }} />
                      </Box>
                      <Box sx={{ height: 4, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.08)' }}>
                        <Box sx={{ width: `${((item.previous_year_qty ?? 0) / maxMonth) * 100}%`, height: 4, borderRadius: 999, bgcolor: '#64748b' }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section>
        <SectionTitle
          title="Productos y líneas que lo consumen"
          sub="Últimos 12 meses. Producido en la base de su regla del escandallo (metros o kg)."
        />
        {detail.consumers_year.length > 0 ? (
          <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {CONSUMER_COLUMNS.map((column) => (
                    <SortableHeaderCell
                      key={column.key}
                      label={column.label}
                      columnKey={column.key}
                      sort={consumerSort}
                      align={column.key === 'product' || column.key === 'line' ? 'left' : 'right'}
                      firstDirection={column.key === 'product' || column.key === 'line' ? 'asc' : 'desc'}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {consumers.map((item) => {
                  const basisUnit = BASIS_UNIT[item.basis] ?? item.basis;
                  return (
                    <TableRow key={`${item.product_id}-${item.line_id ?? ''}-${item.basis}`}>
                      <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>{consumerProductLabel(item)}</TableCell>
                      <TableCell>{item.line_name ?? (item.line_id ? item.line_id : '—')}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtQty(item.produced_qty)} {basisUnit}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.consumption_per_unit === null
                          ? '—'
                          : `${esNumberFormat({ maximumFractionDigits: 4 }).format(item.consumption_per_unit)} ${unit}/${basisUnit}`}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                        {fmtQty(item.consumed_qty)} {unit}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Sin consumo en los últimos 12 meses.</Typography>
        )}
      </Section>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Pestaña Entradas y compras (los pedidos pendientes los pone el diálogo)
// ---------------------------------------------------------------------------

type EntryItem = MaterialDetail['entries']['recent'][number];
type EntrySortKey = 'date' | 'supplier' | 'document' | 'quantity';
// IND-026: el proveedor con su nombre (del albarán o, si falta, de los
// pedidos de compra) y el código al pasar el ratón; sin nombre, el código.
function supplierLabel(item: EntryItem): string | null {
  return item.supplier_name || item.supplier || null;
}
const ENTRY_COLUMNS: Array<{ key: EntrySortKey; label: string; value: (item: EntryItem) => SortValue }> = [
  { key: 'date', label: 'Fecha', value: (item) => item.entry_date },
  { key: 'supplier', label: 'Proveedor', value: supplierLabel },
  { key: 'document', label: 'Albarán', value: (item) => item.document_ref },
  { key: 'quantity', label: 'Cantidad', value: (item) => item.quantity },
];

export function EntriesSection({
  detail,
  failed,
  unit,
  entriesSinceCount,
  baselineDate,
}: {
  detail: MaterialDetail | undefined;
  /** El detalle falló: sin «Calculando…» (el aviso con «Reintentar» va arriba). */
  failed: boolean;
  unit: string;
  entriesSinceCount: number;
  baselineDate: string | null;
}) {
  const entries = detail?.entries;
  const sort = useTableSort<EntrySortKey>('date', 'desc');
  const sortColumn = ENTRY_COLUMNS.find((column) => column.key === sort.key) ?? ENTRY_COLUMNS[0];
  const recent = sortRows(entries?.recent ?? [], sortColumn.value, sort.direction);
  const pendingValue = failed ? '—' : <LoadingValue />;
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
        <Figure
          label="Entradas desde el recuento"
          value={`${fmtQty(entriesSinceCount)} ${unit}`.trim()}
          sub={baselineDate ? `desde el ${fmtDay(baselineDate)}` : 'sin recuento de partida'}
        />
        {/* Decisión 29/10: el mismo periodo que la rotación. */}
        <Figure
          label="Entradas del periodo"
          value={entries ? `${fmtQty(entries.last_3m_qty)} ${unit}`.trim() : pendingValue}
          sub={
            entries
              ? `desde el ${shortDay(entries.period_from)} · consumo del mismo periodo: ${fmtQty(entries.consumed_3m_qty)} ${unit}`
              : undefined
          }
        />
        <Figure
          label="Entradas frente a consumo"
          value={
            !entries
              ? pendingValue
              : entries.entries_vs_consumption === null
                ? '—'
                : esNumberFormat({ maximumFractionDigits: 2 }).format(entries.entries_vs_consumption)
          }
          sub={`1 = entra lo mismo que se consume (desde el ${entries ? shortDay(entries.period_from) : '—'})`}
        />
      </Box>

      <Section>
        <SectionTitle title="Últimos albaranes" sub="Los 10 más recientes" />
        {!detail ? (
          failed ? (
            <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>—</Typography>
          ) : (
            <DetailLoading height={120} />
          )
        ) : recent.length > 0 ? (
          <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {ENTRY_COLUMNS.map((column) => (
                    <SortableHeaderCell
                      key={column.key}
                      label={column.label}
                      columnKey={column.key}
                      sort={sort}
                      align={column.key === 'quantity' ? 'right' : 'left'}
                      firstDirection={column.key === 'supplier' || column.key === 'document' ? 'asc' : 'desc'}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map((item, index) => (
                  <TableRow key={`${item.entry_date}-${item.document_ref ?? ''}-${index}`}>
                    <TableCell>{fmtDay(item.entry_date)}</TableCell>
                    <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                      {item.supplier_name && item.supplier ? (
                        <MuiTooltip title={`Código ${item.supplier}`} placement="top-start">
                          <span>{item.supplier_name}</span>
                        </MuiTooltip>
                      ) : (
                        supplierLabel(item) || '—'
                      )}
                    </TableCell>
                    <TableCell>{item.document_ref || '—'}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: item.quantity < 0 ? '#fca5a5' : undefined }}
                    >
                      {fmtQty(item.quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Sin albaranes de este material.</Typography>
        )}
      </Section>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Pestaña Datos: notas del histórico
// ---------------------------------------------------------------------------

export function HistoryNotes({ detail }: { detail: MaterialDetail }) {
  const notes: string[] = [];
  if (detail.history_from) {
    notes.push(`El histórico de consumo empieza el ${fmtDay(detail.history_from)} (primer parte de producción de la planta); antes, «sin datos».`);
  }
  if (detail.notes.undated_production_rows > 0) {
    notes.push(
      `${detail.notes.undated_production_rows} ${detail.notes.undated_production_rows === 1 ? 'fila' : 'filas'} de producción mensual sin fecha de productos que lo usan no ${detail.notes.undated_production_rows === 1 ? 'entra' : 'entran'} en el histórico (ni en la rotación, aunque sí en el stock de hoy).`,
    );
  }
  if (detail.notes.boxes_resolved_with_today_personalization) {
    notes.push('Es una caja real: el consumo histórico se reparte con la personalización de cajas de hoy.');
  }
  if (notes.length === 0) return null;
  return (
    <Section>
      <SectionTitle title="Sobre el histórico" />
      <Stack spacing={0.5}>
        {notes.map((note) => (
          <Typography key={note} sx={{ color: '#cbd5e1', fontSize: 12 }}>
            {note}
          </Typography>
        ))}
      </Stack>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// IND-025: rotación, regularidad, clase ABC, recuentos y «Lo importante»
// ---------------------------------------------------------------------------

const REGULARITY_LABEL: Record<MaterialDetail['regularity']['label'], string> = {
  regular: 'Regular',
  irregular: 'Irregular',
  few_data: 'Pocos datos',
  no_consumption: 'Sin consumo',
};

type RotationPeriod = MaterialDetail['rotation']['recent'];

// Días de rotación o «—» con el motivo.
export function rotationValue(period: RotationPeriod): { value: string; note: string | null } {
  // Cambio IND-028: el motivo con fechas, el mismo que en la tabla del Panel.
  if (period.too_many_negative || period.computed_from || period.effective_from === null) {
    return {
      value: '—',
      note: `— · ${rotationGapText({ ...period, has_base: period.effective_from !== null, missing_days: period.missing_days })}`,
    };
  }
  if (period.average_stock === null) return { value: '—', note: 'sin recuento en el periodo' };
  if (period.rotation_days === null) return { value: '—', note: 'sin consumo en el periodo' };
  return {
    value: `${fmtQty(period.rotation_days)} días`,
    note:
      [
        // IND-028: periodo menor de 3 meses (desde el primer día con recuento).
        period.partial && period.effective_from ? `desde ${shortDay(period.effective_from)}` : null,
        period.times_per_year !== null ? `${fmtQty(period.times_per_year)} veces al año` : null,
      ]
        .filter(Boolean)
        .join(' · ') || null,
  };
}

export function RotationSection({ detail, unit }: { detail: MaterialDetail; unit: string }) {
  const rows: Array<{ label: string; period: RotationPeriod }> = [
    { label: 'Últimos 3 meses', period: detail.rotation.recent },
    { label: '3 meses anteriores', period: detail.rotation.previous },
  ];
  return (
    <Section>
      <SectionTitle
        title="Rotación y movimiento"
        sub="Stock medio del periodo (stock diario reconstruido: recuento + entradas − consumo; los días negativos cuentan 0) ÷ consumo por día laborable. Veces al año = días laborables del año ÷ días de rotación."
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Periodo</TableCell>
              <TableCell align="right">Stock medio</TableCell>
              <TableCell align="right">Consumo por día laborable</TableCell>
              <TableCell align="right">Días de rotación</TableCell>
              <TableCell align="right">Veces al año</TableCell>
              <TableCell>Nota</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ label, period }) => {
              const rotation = rotationValue(period);
              const computed = rotation.value !== '—';
              return (
                <TableRow key={label}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {label}
                    <Typography component="span" sx={{ color: '#94a3b8', fontSize: 10, ml: 0.75 }}>
                      {/* IND-028: con periodo recortado, desde el primer día con recuento. */}
                      {`${shortDay(period.partial && period.effective_from ? period.effective_from : period.from)}–${shortDay(period.to)}`}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {period.average_stock === null ? '—' : `${fmtQty(period.average_stock)} ${unit}`.trim()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {period.per_workday === null ? '—' : `${fmtQty(period.per_workday)} ${unit}`.trim()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                    {rotation.value}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {computed ? fmtQty(period.times_per_year) : '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontSize: 11.5 }}>
                    {computed ? (period.partial ? 'menos de 3 meses: desde el primer recuento' : null) : rotation.note}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  );
}

// Cabecera: «Clase A · 14 % del consumo de materias primas (kg)».
export function abcLabel(abc: MaterialDetail['abc']): string | null {
  if (!abc) return null;
  if (!abc.class) return 'sin clase (sin consumo en 12 meses)';
  return `Clase ${abc.class} · ${fmtPct(abc.share_pct)} del consumo de ${abc.group_label}, 12 meses`;
}

export function HighlightsSection({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(56,189,248,0.06)',
        border: '1px solid rgba(56,189,248,0.25)',
      }}
    >
      <Typography sx={{ color: '#38bdf8', fontWeight: 900, fontSize: 12.5, mb: 0.5 }}>Lo importante</Typography>
      <Stack component="ul" spacing={0.4} sx={{ m: 0, pl: 2.25 }}>
        {items.map((item) => (
          <Typography component="li" key={item} sx={{ color: '#e2e8f0', fontSize: 12.5 }}>
            {item}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

const RELIABILITY_COLOR: Record<'alta' | 'media' | 'baja', string> = {
  alta: '#4ade80',
  media: '#fbbf24',
  baja: '#f87171',
};

const COMPARISON_STATUS: Record<MaterialDetail['counts']['comparisons'][number]['status'], string | null> = {
  compared: null,
  calculated_le_zero: 'calculado ≤ 0: no cuenta para la fiabilidad',
  not_counted: 'no está en el recuento',
  no_base: 'sin cálculo ese día',
};

// Stock ajustado = stock hoy × (1 + media de la diferencia).
export function adjustedStock(stock: number | null, reliability: MaterialDetail['counts']['reliability']): number | null {
  if (stock === null || !Number.isFinite(stock) || !reliability) return null;
  return stock * (1 + reliability.mean_pct / 100);
}

export function CountsTab({ detail, stock, unit }: { detail: MaterialDetail; stock: number | null; unit: string }) {
  const { comparisons, reliability } = detail.counts;
  const adjusted = adjustedStock(stock, reliability);
  if (comparisons.length === 0) {
    return (
      <Section>
        <SectionTitle title="Recuentos y fiabilidad" />
        <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
          Este material aún no tiene recuentos comparados con lo calculado (se comparan al cargar un recuento; ver
          Datos de planta › Diferencia de inventario).
        </Typography>
      </Section>
    );
  }
  return (
    <Stack spacing={1.5}>
      {reliability ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
          <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2, minWidth: 0 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 800 }}>Fiabilidad</Typography>
            <Typography sx={{ mt: 0.3, fontSize: 17, fontWeight: 950, color: RELIABILITY_COLOR[reliability.level] }}>
              {reliability.level.charAt(0).toUpperCase() + reliability.level.slice(1)}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 10.5, mt: 0.3 }}>
              {/* IND-027: el nivel sale de la desviación; el sesgo ajusta el stock. */}
              {`desviación media ±${fmtPct(reliability.mean_abs_pct)} · sesgo ${fmtPct(reliability.mean_pct, true)} en ${reliability.count_dates.length} ${reliability.count_dates.length === 1 ? 'recuento' : 'recuentos'} · alta ≤ 2 %, media ≤ 5 %`}
            </Typography>
          </Paper>
          <Figure label="Stock hoy" value={`${fmtQty(stock)} ${unit}`.trim()} sub="calculado" />
          <Figure
            label="Stock ajustado"
            value={`${fmtQty(adjusted)} ${unit}`.trim()}
            sub={`stock hoy × (1 ${reliability.mean_pct < 0 ? '−' : '+'} ${fmtPct(Math.abs(reliability.mean_pct))})`}
          />
        </Box>
      ) : null}
      <Section>
        <SectionTitle
          title="Recuentos comparados"
          sub="Lo que PROA calculaba el día de cada recuento frente a lo contado (como en Diferencia de inventario). La fiabilidad usa los 3 últimos con calculado > 0."
        />
        <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Recuento</TableCell>
                <TableCell align="right">Calculado</TableCell>
                <TableCell align="right">Contado</TableCell>
                <TableCell align="right">Diferencia</TableCell>
                <TableCell align="right">%</TableCell>
                <TableCell>Nota</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisons.map((item) => (
                <TableRow key={item.count_date}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDay(item.count_date)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(item.calculated_qty)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(item.counted_qty)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(item.difference_qty)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtPct(item.difference_pct, true)}
                  </TableCell>
                  <TableCell sx={{ color: '#94a3b8', fontSize: 11.5 }}>{COMPARISON_STATUS[item.status]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Stack>
  );
}
